import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { Swords } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { SocketProvider } from './contexts/SocketContext';
import { TutorialProvider } from './contexts/TutorialContext';
import Layout from './components/Layout';
import ProtectedStoreRouteBySlug from './components/ProtectedStoreRouteBySlug';
import SeoManager from './components/SeoManager';
import { detectCatalogSubdomain, getMainPlatformUrl } from './features/catalogo/utils/subdomain';

const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const Profile = React.lazy(() => import('./pages/Profile'));
const GarageSaleDashboard = React.lazy(() => import('./pages/GarageSaleDashboard'));
const Lobby = React.lazy(() => import('./pages/Lobby'));
const CommunityLobby = React.lazy(() => import('./pages/CommunityLobby'));
const BingoLobby = React.lazy(() => import('./pages/BingoLobby'));
const TicTacToeLobby = React.lazy(() => import('./pages/TicTacToeLobby'));
const TicTacToeRoom = React.lazy(() => import('./pages/TicTacToeRoom'));
const RafflesLobby = React.lazy(() => import('./features/raffles/pages/RafflesLobby'));
const RaffleRoom = React.lazy(() => import('./features/raffles/pages/RaffleRoom'));
const MyRaffles = React.lazy(() => import('./features/raffles/pages/MyRaffles'));
const RafflePublicLanding = React.lazy(() => import('./features/raffles/pages/RafflePublicLanding'));
const Landing = React.lazy(() => import('./pages/Landing'));
const SalesLanding = React.lazy(() => import('./pages/SalesLanding'));
const DesaparecidosVenezuela = React.lazy(() => import('./pages/DesaparecidosVenezuela'));
const AyudaMutua = React.lazy(() => import('./pages/AyudaMutua'));
const Transparencia = React.lazy(() => import('./pages/Transparencia'));
const Cronologia = React.lazy(() => import('./pages/Cronologia'));
const GiftLinkClaimPage = React.lazy(() => import('./pages/GiftLinkClaimPage'));
const TitoLanding = React.lazy(() => import('./pages/TitoLanding'));
const Changelog = React.lazy(() => import('./pages/Changelog'));
const MercadeoDashboard = React.lazy(() => import('./pages/mercadeo/MercadeoDashboard'));
const PlannerPage = React.lazy(() => import('./pages/mercadeo/PlannerPage'));
const TransportQrPaymentPage = React.lazy(() => import('./pages/TransportQrPaymentPage'));

// --- DEMO CATALOGO ---
const CatalogoDemo = React.lazy(() => import('./features/catalogo/pages/Catalog'));
const CatalogoAdminDemo = React.lazy(() => import('./features/catalogo/pages/Admin'));
const CatalogCreationWizard = React.lazy(() => import('./components/admin/CatalogCreationWizard'));
const CatalogCreationWizardPage = React.lazy(() => import('./pages/CatalogCreationWizardPage'));
const AdminCatalogPluginsModal = React.lazy(() => import('./components/admin/AdminCatalogPluginsModal'));
const AdminCatalogMaintenanceModal = React.lazy(() => import('./components/admin/AdminCatalogMaintenanceModal'));
const AdminIndependentCatalogs = React.lazy(() => import('./components/admin/AdminIndependentCatalogs'));
const PdfImportFloatingBar = React.lazy(() => import('./features/catalogo/components/PdfImportFloatingBar'));
// ---------------------
const SeoStoresDirectory = React.lazy(() => import('./pages/seo/SeoStoresDirectory'));
const SeoBingoLanding = React.lazy(() => import('./pages/seo/SeoBingoLanding'));
const SeoRafflesLanding = React.lazy(() => import('./pages/seo/SeoRafflesLanding'));
const SeoTransparency = React.lazy(() => import('./pages/seo/SeoTransparency'));
const SeoFaq = React.lazy(() => import('./pages/seo/SeoFaq'));
const LegalPages = React.lazy(() => import('./pages/legal/LegalPages'));

const Games = React.lazy(() => import('./pages/Games'));
const Market = React.lazy(() => import('./pages/Market'));
const MarketPlaceDetail = React.lazy(() => import('./pages/MarketPlaceDetail'));
const Admin = React.lazy(() => import('./pages/Admin'));
const StoreOwnerDashboard = React.lazy(() => import('./pages/store/StoreOwnerDashboard'));
const CVBuilderPage = React.lazy(() => import('./modules/cv-builder/CVBuilderPage'));
const TitoDashboard = React.lazy(() => import('./pages/TitoDashboard'));
const TransportPage = React.lazy(() => import('./pages/TransportPage'));
const POS = React.lazy(() => import('./pages/store/POS'));
const StoreFront = React.lazy(() => import('./pages/store/StoreFront'));
const StoreFrontInvoicePage = React.lazy(() => import('./pages/store/StoreFrontInvoicePage'));
const OrderCatalogPdfPublicPage = React.lazy(() => import('./pages/store/OrderCatalogPdfPublicPage'));
const StoreQrPaymentPage = React.lazy(() => import('./pages/store/StoreQrPaymentPage'));
const StoreBatchCloseReportPage = React.lazy(() => import('./pages/store/StoreBatchCloseReportPage'));
const KitchenDisplay = React.lazy(() => import('./pages/store/KitchenDisplay'));
const BingoV2WaitingRoom = React.lazy(() => import('./pages/BingoV2WaitingRoom'));
const BingoV2GameRoom = React.lazy(() => import('./pages/BingoV2GameRoom'));
const CaidaLobby = React.lazy(() => import('./pages/CaidaLobby'));
const CaidaRoom = React.lazy(() => import('./pages/CaidaRoom'));
const RouletteLobby = React.lazy(() => import('./pages/RouletteLobby'));
const RouletteRoom = React.lazy(() => import('./pages/RouletteRoom'));
const PokerLobby = React.lazy(() => import('./pages/PokerLobby'));
const PokerTable = React.lazy(() => import('./pages/PokerTable'));
const TowerDefense = React.lazy(() => import('./components/TowerDefense/TowerDefense'));
const MobaArenaLobby = React.lazy(() => import('./pages/MobaArenaLobby'));
const MobaArenaRoom = React.lazy(() => import('./pages/MobaArenaRoom'));
const MobaArenaGame = React.lazy(() => import('./components/MobaArena/MobaArenaGame'));
const MobaArenaRanking = React.lazy(() => import('./pages/MobaArenaRanking'));
const UnifiedInventory = React.lazy(() => import('./components/GameInventory/UnifiedInventory'));
const UnifiedMarket = React.lazy(() => import('./components/GameInventory/UnifiedMarket'));
const StoryMode = React.lazy(() => import('./components/StoryMode/StoryMode'));
const StoryRouter = React.lazy(() => import('./components/StoryMode/StoryRouter'));
const StoryDefense = React.lazy(() => import('./components/StoryMode/StoryDefense'));
const StoryDungeon = React.lazy(() => import('./components/StoryMode/StoryDungeon'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        // Don't retry on 429 (handled by interceptor) or 401/403
        if (status === 429 || status === 401 || status === 403) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      staleTime: 30 * 1000, // Reduced from 5m to 30s for better data freshness in store dashboards
    },
  },
});

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <div className="spinner"></div>
      </div>
    );
  }

  // Permitir acceso público a Catálogos, StoreFront (/store/:slug), facturas y páginas legales
  const path = location.pathname;
  const publicStorefrontRoutes = [
    /^\/store\/[^/]+(?:\/(invoice|qr)(?:\/[^/]+)?)?\/?$/i,
    /^\/transport\/pay\/[^/]+\/?$/i,
    /^\/market(?:\/.*)?$/i,
    /^\/(terminos|terms|privacidad|privacy|dmca)\/?$/i,
    /^\/c\/[^/]+(?:\/.*)?$/i,
    /^\/catalogo(?:\/.*)?$/i,
    /^\/(belenshop|hotel-aguamarina|aguamarina|maho-albi|mahoalbi|gamingspot|roel)(?:\/.*)?$/i,
    /^\/crear-(catalogo|tienda)\/?$/i,
  ];
  const isPublicStoreFront = publicStorefrontRoutes.some((re) => re.test(path));

  if (!user && !isPublicStoreFront) {
    const nextPath = `${location.pathname}${location.search}`;
    const target = `/login?next=${encodeURIComponent(nextPath)}`;
    return <Navigate to={target} replace />;
  }

  return children;
}

function HomeRedirect() {
  const target = '/lobby';
  return <Navigate to={target} replace />;
}

// Galactic Arena access guard: same admin/tote/tito rules as the rest of the app
function MobaArenaGuard({ children }) {
  const { user, loading, isAdmin, isTote } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <div className="spinner" />
      </div>
    );
  }
  const username = (user?.username || '').toLowerCase().trim();
  const hasTitoRole = user?.roles?.includes('tito');
  const isAllowed = isAdmin() || isTote() || username === 'tito' || hasTitoRole;
  if (!isAllowed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-dvh bg-background-dark text-text p-6 text-center">
        <Swords size={48} className="text-amber-500 mb-4" />
        <h2 className="text-2xl font-bold mb-2">Galactic Arena - Acceso Restringido</h2>
        <p className="text-text/60 mb-4">Este modo está en desarrollo. Solo usuarios autorizados pueden acceder.</p>
        <button className="btn-primary" onClick={() => window.location.href = '/games'}>Volver a Juegos</button>
      </div>
    );
  }
  return children;
}

// Guard for Games & Raffles: only visible/accessible for tote, tito and admin
function GamesAndRafflesGuard({ children }) {
  const { user, loading, isAdmin, isTote } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <div className="spinner" />
      </div>
    );
  }
  const username = (user?.username || '').toLowerCase().trim();
  const isToteUser = (typeof isTote === 'function' ? isTote() : false) || user?.roles?.includes('tote') || username === 'tote' || username === 'wilcnct' || user?.tg_id?.toString() === '1417856820';
  const isTitoUser = username === 'tito' || user?.roles?.includes('tito');
  const isAdminUser = (typeof isAdmin === 'function' ? isAdmin() : false) || user?.roles?.includes('admin') || user?.role === 'admin' || username === 'admin';
  const isAllowed = isToteUser || isTitoUser || isAdminUser;

  if (!isAllowed) {
    return <Navigate to="/lobby" replace />;
  }
  return children;
}

function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="spinner"></div>
    </div>
  );
}

function LazyRoute({ children }) {
  return (
    <Suspense fallback={<RouteFallback />}>
      {children}
    </Suspense>
  );
}

function ChatXyzRedirect() {
  useEffect(() => {
    const host = window.location.host;
    const protocol = window.location.protocol;

    // En producción: usar la variable de entorno o la URL del servicio de chat en Railway
    // En desarrollo local: setupProxy.js maneja /chatxyz → localhost:3015 automáticamente
    const chatUrl = process.env.REACT_APP_CHAT_URL || `${protocol}//${host}/chatxyz`;
    window.location.href = chatUrl;
  }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background-dark text-text">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
      <p className="text-text/60">Redirigiendo a MundoXYZ Chat...</p>
    </div>
  );
}

function SubdomainToMainRedirect() {
  const location = useLocation();
  useEffect(() => {
    const targetUrl = getMainPlatformUrl(location.pathname + location.search + location.hash);
    window.location.href = targetUrl;
  }, [location]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background-dark text-text">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
      <p className="text-text/60">Redirigiendo a la plataforma principal...</p>
    </div>
  );
}

function App() {
  useEffect(() => {
    // Set viewport height for mobile
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);

    // Initialize appearance theme from localStorage
    try {
      const stored = typeof window !== 'undefined'
        ? window.localStorage.getItem('app_theme')
        : null;
      const theme = stored || 'orion';
      document.documentElement.dataset.theme = theme;
    } catch {
      document.documentElement.dataset.theme = 'orion';
    }

    // Capture and persist referral code from URL (?ref=) with 7-day TTL
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get('ref') || '').trim();
      if (ref && typeof window !== 'undefined') {
        const payload = { code: ref, ts: Date.now() };
        window.localStorage.setItem('mx_referral_code', JSON.stringify(payload));
      }
      // Clean up expired referral codes (> 7 days)
      const raw = window.localStorage.getItem('mx_referral_code');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Date.now() - (parsed.ts || 0) > 7 * 24 * 60 * 60 * 1000) {
          window.localStorage.removeItem('mx_referral_code');
        }
      }
    } catch {}

    // Auto-reload on stale chunk errors (cache mismatch after deploy)
    const isChunkErrorMsg = (msg) => {
      if (!msg) return false;
      const str = String(msg);
      return (
        str.includes('ChunkLoadError') ||
        str.includes('Loading chunk') ||
        str.includes('Loading CSS chunk') ||
        str.includes('Failed to fetch dynamically imported module') ||
        str.includes('Unexpected token') ||
        str.includes('Importing a module script failed') ||
        str.includes('error loading dynamically imported module') ||
        str.includes('Script error')
      );
    };

    const handleChunkError = (event) => {
      const msg = event?.message || event?.error?.message || '';
      if (isChunkErrorMsg(msg)) {
        if (!window.sessionStorage.getItem('chunk_reload_done')) {
          window.sessionStorage.setItem('chunk_reload_done', '1');
          window.location.reload();
        }
      }
    };

    const handleUnhandledRejection = (event) => {
      const reason = event?.reason;
      const msg = (reason && (reason.message || reason.name || String(reason))) || '';
      if (isChunkErrorMsg(msg)) {
        if (!window.sessionStorage.getItem('chunk_reload_done')) {
          window.sessionStorage.setItem('chunk_reload_done', '1');
          window.location.reload();
        }
      }
    };

    window.addEventListener('error', handleChunkError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('error', handleChunkError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const catalogSubdomainSlug = React.useMemo(() => detectCatalogSubdomain(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <SocketProvider>
            <Router>
            <SeoManager />
            <TutorialProvider>
            <div className="App min-h-screen bg-background-dark text-text font-display">
              <Toaster
                position="top-center"
                toastOptions={{
                  duration: 2500,
                  style: {
                    background: 'var(--theme-card, #FFFFFF)',
                    color: 'var(--theme-text, #202124)',
                    border: '1px solid var(--theme-border, #DADCE0)',
                    borderRadius: '1rem',
                    boxShadow: '0 4px 16px rgba(60, 64, 67, 0.18), 0 1px 3px rgba(60, 64, 67, 0.12)',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '10px 16px',
                  },
                  success: {
                    iconTheme: {
                      primary: '#1E8E3E',
                      secondary: '#FFFFFF',
                    },
                  },
                  error: {
                    iconTheme: {
                      primary: '#D93025',
                      secondary: '#FFFFFF',
                    },
                  },
                }}
              />

              {catalogSubdomainSlug ? (
                <Routes>
                  {/* --- SUBDOMINIO DE CATÁLOGO (MARCA BLANCA MULTI-TENANT) --- */}
                  <Route path="/" element={<LazyRoute><CatalogoDemo storeSlug={catalogSubdomainSlug} /></LazyRoute>} />
                  <Route path="/admin" element={<LazyRoute><CatalogoAdminDemo storeSlug={catalogSubdomainSlug} /></LazyRoute>} />
                  <Route path="/crear-catalogo" element={<LazyRoute><CatalogCreationWizardPage /></LazyRoute>} />
                  <Route path="/crear-tienda" element={<LazyRoute><CatalogCreationWizardPage /></LazyRoute>} />
                  <Route path="/pedido/:orderId" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />
                  <Route path="/pedido/:orderId/pdf" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />
                  <Route path="/pedido/:orderId/catalogo-pdf" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />
                  <Route path="/c/:storeSlug/pedido/:orderId" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />
                  <Route path="/c/:storeSlug/pedido/:orderId/pdf" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />
                  <Route path="/c/:storeSlug/pedido/:orderId/catalogo-pdf" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />
                  <Route path="/c/:storeSlug/invoice/:orderId" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />
                  <Route path="/invoice/:orderId" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />
                  <Route path="/c/:storeSlug" element={<LazyRoute><CatalogoDemo /></LazyRoute>} />
                  <Route path="/c/:storeSlug/admin" element={<LazyRoute><CatalogoAdminDemo /></LazyRoute>} />
                  {/* Rutas de la plataforma principal que deben escapar del subdominio */}
                  <Route path="/store/*" element={<SubdomainToMainRedirect />} />
                  <Route path="/login" element={<SubdomainToMainRedirect />} />
                  <Route path="/register" element={<SubdomainToMainRedirect />} />
                  <Route path="/lobby" element={<SubdomainToMainRedirect />} />
                  <Route path="/profile" element={<SubdomainToMainRedirect />} />
                  <Route path="/:storeSlug" element={<LazyRoute><CatalogoDemo storeSlug={catalogSubdomainSlug === 'default' ? undefined : catalogSubdomainSlug} /></LazyRoute>} />
                  <Route path="/:storeSlug/admin" element={<LazyRoute><CatalogoAdminDemo storeSlug={catalogSubdomainSlug === 'default' ? undefined : catalogSubdomainSlug} /></LazyRoute>} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              ) : (
                <Routes>
                  {/* Inicio Principal (SalesLanding) */}
                  <Route path="/" element={<LazyRoute><SalesLanding /></LazyRoute>} />
                  <Route path="/empresas" element={<Navigate to="/" replace />} />
                  <Route path="/propuesta" element={<Navigate to="/" replace />} />

                  {/* Ventas (Landing anterior) */}
                  <Route path="/ventas" element={<LazyRoute><Landing /></LazyRoute>} />
                  <Route path="/landing" element={<Navigate to="/ventas" replace />} />
                  <Route path="/Landing" element={<Navigate to="/ventas" replace />} />

                {/* Desaparecidos Venezuela - Directorio público */}
                <Route path="/desaparecidosvenezuela" element={<LazyRoute><DesaparecidosVenezuela /></LazyRoute>} />
                <Route path="/ayuda-mutua" element={<LazyRoute><AyudaMutua /></LazyRoute>} />
                <Route path="/transparencia" element={<LazyRoute><Transparencia /></LazyRoute>} />
                <Route path="/cronologia" element={<LazyRoute><Cronologia /></LazyRoute>} />

                {/* Landing informativa para Tito (sin login) */}
                <Route path="/tito/info" element={<LazyRoute><TitoLanding /></LazyRoute>} />

                {/* Landing Pública de Rifas (sin login) */}
                <Route path="/raffles/public/:code" element={<LazyRoute><RafflePublicLanding /></LazyRoute>} />

                {/* Changelog público */}
                <Route path="/changelog" element={<LazyRoute><Changelog /></LazyRoute>} />

                {/* Dashboard Mercadeo (público, sin login) */}
                <Route path="/mercadeo" element={<LazyRoute><MercadeoDashboard /></LazyRoute>} />

                {/* SEO Public Pages */}
                <Route path="/stores" element={<LazyRoute><SeoStoresDirectory /></LazyRoute>} />
                <Route path="/bingo-online" element={<LazyRoute><SeoBingoLanding /></LazyRoute>} />
                <Route path="/rifas" element={<LazyRoute><SeoRafflesLanding /></LazyRoute>} />
                <Route path="/transparencia" element={<LazyRoute><SeoTransparency /></LazyRoute>} />
                <Route path="/faq" element={<LazyRoute><SeoFaq /></LazyRoute>} />

                {/* Marco Legal, Privacidad & DMCA */}
                <Route path="/terminos" element={<LazyRoute><LegalPages initialTab="terminos" /></LazyRoute>} />
                <Route path="/terms" element={<Navigate to="/terminos" replace />} />
                <Route path="/privacidad" element={<LazyRoute><LegalPages initialTab="privacidad" /></LazyRoute>} />
                <Route path="/privacy" element={<Navigate to="/privacidad" replace />} />
                <Route path="/dmca" element={<LazyRoute><LegalPages initialTab="dmca" /></LazyRoute>} />

                {/* --- PLANTILLA BASE / CATÁLOGO MAESTRO (DEFAULT) --- */}
                <Route path="/catalogo" element={<LazyRoute><CatalogoDemo storeSlug="default" /></LazyRoute>} />
                <Route path="/catalogo/admin" element={<LazyRoute><CatalogoAdminDemo storeSlug="default" /></LazyRoute>} />
                <Route path="/default" element={<Navigate to="/catalogo" replace />} />
                <Route path="/default/admin" element={<Navigate to="/catalogo/admin" replace />} />
                <Route path="/c/default" element={<Navigate to="/catalogo" replace />} />
                <Route path="/c/default/admin" element={<Navigate to="/catalogo/admin" replace />} />
                {/* ---------------------------------------------------- */}

                {/* --- HOTEL AGUAMARINA CATÁLOGO INDEPENDIENTE --- */}
                <Route path="/hotel-aguamarina" element={<LazyRoute><CatalogoDemo storeSlug="hotel-aguamarina" /></LazyRoute>} />
                <Route path="/hotel-aguamarina/admin" element={<LazyRoute><CatalogoAdminDemo storeSlug="hotel-aguamarina" /></LazyRoute>} />
                <Route path="/aguamarina" element={<LazyRoute><CatalogoDemo storeSlug="hotel-aguamarina" /></LazyRoute>} />
                <Route path="/aguamarina/admin" element={<LazyRoute><CatalogoAdminDemo storeSlug="hotel-aguamarina" /></LazyRoute>} />
                <Route path="/hotelaguamarina" element={<Navigate to="/hotel-aguamarina" replace />} />
                <Route path="/hotelaguamarina/admin" element={<Navigate to="/hotel-aguamarina/admin" replace />} />
                <Route path="/menu-hotel-aguamarina" element={<Navigate to="/hotel-aguamarina" replace />} />
                <Route path="/menu-hotel-aguamarina/admin" element={<Navigate to="/hotel-aguamarina/admin" replace />} />
                <Route path="/c/hotel-aguamarina" element={<Navigate to="/hotel-aguamarina" replace />} />
                <Route path="/c/hotel-aguamarina/admin" element={<Navigate to="/hotel-aguamarina/admin" replace />} />
                <Route path="/c/aguamarina" element={<Navigate to="/hotel-aguamarina" replace />} />
                <Route path="/c/aguamarina/admin" element={<Navigate to="/hotel-aguamarina/admin" replace />} />
                {/* ------------------------------------------------ */}

                {/* --- BELENSHOP CATÁLOGO INDEPENDIENTE --- */}
                <Route path="/belenshop" element={<LazyRoute><CatalogoDemo storeSlug="belenshop" /></LazyRoute>} />
                <Route path="/belenshop/admin" element={<LazyRoute><CatalogoAdminDemo storeSlug="belenshop" /></LazyRoute>} />
                <Route path="/c/belenshop" element={<Navigate to="/belenshop" replace />} />
                <Route path="/c/belenshop/admin" element={<Navigate to="/belenshop/admin" replace />} />
                {/* --------------------------------------- */}

                {/* --- MAHOLY + ALBIMAR CATÁLOGO INDEPENDIENTE --- */}
                <Route path="/maho-albi" element={<LazyRoute><CatalogoDemo storeSlug="maho-albi" /></LazyRoute>} />
                <Route path="/maho-albi/admin" element={<LazyRoute><CatalogoAdminDemo storeSlug="maho-albi" /></LazyRoute>} />
                <Route path="/mahoalbi" element={<Navigate to="/maho-albi" replace />} />
                <Route path="/mahoalbi/admin" element={<Navigate to="/maho-albi/admin" replace />} />
                <Route path="/c/maho-albi" element={<Navigate to="/maho-albi" replace />} />
                <Route path="/c/maho-albi/admin" element={<Navigate to="/maho-albi/admin" replace />} />
                <Route path="/c/mahoalbi" element={<Navigate to="/maho-albi" replace />} />
                <Route path="/c/mahoalbi/admin" element={<Navigate to="/maho-albi/admin" replace />} />
                {/* ----------------------------------------------- */}

                {/* --- GAMING SPOT C. A CATÁLOGO INDEPENDIENTE --- */}
                <Route path="/gamingspot" element={<LazyRoute><CatalogoDemo storeSlug="gamingspot" /></LazyRoute>} />
                <Route path="/gamingspot/admin" element={<LazyRoute><CatalogoAdminDemo storeSlug="gamingspot" /></LazyRoute>} />
                <Route path="/gaming-spot" element={<Navigate to="/gamingspot" replace />} />
                <Route path="/gaming-spot/admin" element={<Navigate to="/gamingspot/admin" replace />} />
                <Route path="/c/gaming-spot" element={<Navigate to="/gamingspot" replace />} />
                <Route path="/c/gaming-spot/admin" element={<Navigate to="/gamingspot/admin" replace />} />
                {/* ----------------------------------------------- */}

                {/* --- ROEL JOYAS VENEZUELA CATÁLOGO INDEPENDIENTE --- */}
                <Route path="/roel" element={<LazyRoute><CatalogoDemo storeSlug="roel" /></LazyRoute>} />
                <Route path="/roel/admin" element={<LazyRoute><CatalogoAdminDemo storeSlug="roel" /></LazyRoute>} />
                <Route path="/c/roel" element={<Navigate to="/roel" replace />} />
                <Route path="/c/roel/admin" element={<Navigate to="/roel/admin" replace />} />
                {/* --------------------------------------------------- */}

                {/* --- RUTA DINÁMICA MULTI-TIENDA PARA NUEVOS CLIENTES --- */}
                <Route path="/c/:storeSlug" element={<LazyRoute><CatalogoDemo /></LazyRoute>} />
                <Route path="/c/:storeSlug/admin" element={<LazyRoute><CatalogoAdminDemo /></LazyRoute>} />
                <Route path="/wizard-demo" element={<LazyRoute><CatalogCreationWizard isOpen={true} onClose={() => {}} /></LazyRoute>} />
                <Route path="/plugins-demo" element={<LazyRoute><AdminCatalogPluginsModal catalog={{ id: 1, store_slug: 'belenshop', business_name: 'BelenShop', whatsapp_phone: '584122098288', plugins: { delivery: { enabled: true }, wholesale: { enabled: true, mode: 'global', min_units: 6 }, ai_agent: { enabled: true, sales_whatsapp: '584122098288', personality: 'Eres Pepe, el asesor comercial estrella de BelenShop.' } } }} onClose={() => {}} onSuccess={() => {}} /></LazyRoute>} />
                <Route path="/maintenance-demo" element={<LazyRoute><AdminCatalogMaintenanceModal catalog={{ id: 1, store_slug: 'belenshop', business_name: 'BelenShop', whatsapp_phone: '584122098288', is_maintenance: true, maintenance_message: 'Estamos actualizando nuestro catálogo e inventario para brindarte una mejor experiencia. Volveremos muy pronto.', maintenance_until: 'Hoy a las 4:00 PM' }} onClose={() => {}} onSuccess={() => {}} /></LazyRoute>} />
                <Route path="/crear-catalogo" element={<LazyRoute><CatalogCreationWizardPage /></LazyRoute>} />
                <Route path="/crear-tienda" element={<LazyRoute><CatalogCreationWizardPage /></LazyRoute>} />
                <Route path="/stores-demo" element={<LazyRoute><div className="min-h-screen bg-slate-950 p-6"><AdminIndependentCatalogs /></div></LazyRoute>} />
                {/* -------------------------------------------------------- */}

                <Route path="/gift-link/:token" element={<LazyRoute><GiftLinkClaimPage /></LazyRoute>} />
                <Route path="/pedido/:orderId" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />
                <Route path="/pedido/:orderId/pdf" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />
                <Route path="/pedido/:orderId/catalogo-pdf" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />
                <Route path="/c/:storeSlug/pedido/:orderId" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />
                <Route path="/c/:storeSlug/pedido/:orderId/pdf" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />
                <Route path="/c/:storeSlug/pedido/:orderId/catalogo-pdf" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />
                <Route path="/c/:storeSlug/invoice/:orderId" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />
                <Route path="/invoice/:orderId" element={<LazyRoute><OrderCatalogPdfPublicPage /></LazyRoute>} />

                <Route path="/chatxyz" element={<ChatXyzRedirect />} />
                <Route path="/login" element={<LazyRoute><Login /></LazyRoute>} />
                <Route path="/register" element={<LazyRoute><Register /></LazyRoute>} />
                <Route path="/registerxyz23" element={<Navigate to="/register" replace />} />
                <Route path="/reset-password" element={<LazyRoute><ResetPassword /></LazyRoute>} />

                {/* Protected Layout and its child routes (pathless layout route) */}
                <Route element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }>
                  <Route path="games" element={<GamesAndRafflesGuard><LazyRoute><Games /></LazyRoute></GamesAndRafflesGuard>} />
                  <Route path="profile" element={<LazyRoute><Profile /></LazyRoute>} />
                  <Route path="messages" element={<Navigate to="/profile?inbox=true" replace />} />
                  <Route path="garage-sales" element={<LazyRoute><GarageSaleDashboard /></LazyRoute>} />
                  <Route path="planner" element={<LazyRoute><PlannerPage /></LazyRoute>} />
                  <Route path="tito" element={<LazyRoute><TitoDashboard /></LazyRoute>} />
                  <Route path="lobby" element={<LazyRoute><Lobby /></LazyRoute>} />
                  <Route path="community-lobby" element={<LazyRoute><CommunityLobby /></LazyRoute>} />
                  <Route path="market" element={<LazyRoute><Market /></LazyRoute>} />
                  <Route path="market/lugar/:slug" element={<LazyRoute><MarketPlaceDetail /></LazyRoute>} />
                  <Route path="market/franquicia/:slug" element={<LazyRoute><MarketPlaceDetail /></LazyRoute>} />
                  <Route path="cv-builder" element={<LazyRoute><CVBuilderPage /></LazyRoute>} />
                  <Route path="bingo" element={<GamesAndRafflesGuard><LazyRoute><BingoLobby /></LazyRoute></GamesAndRafflesGuard>} />
                  <Route path="bingo/v2/room/:code" element={<GamesAndRafflesGuard><LazyRoute><BingoV2WaitingRoom /></LazyRoute></GamesAndRafflesGuard>} />
                  <Route path="bingo/v2/play/:code" element={<GamesAndRafflesGuard><LazyRoute><BingoV2GameRoom /></LazyRoute></GamesAndRafflesGuard>} />
                  <Route path="tictactoe/lobby" element={<LazyRoute><TicTacToeLobby /></LazyRoute>} />
                  <Route path="tictactoe/room/:code" element={<LazyRoute><TicTacToeRoom /></LazyRoute>} />
                  <Route path="caida/lobby" element={<LazyRoute><CaidaLobby /></LazyRoute>} />
                  <Route path="caida/room/:code" element={<LazyRoute><CaidaRoom /></LazyRoute>} />
                  <Route path="roulette/lobby" element={<LazyRoute><RouletteLobby /></LazyRoute>} />
                  <Route path="roulette/room/:code" element={<LazyRoute><RouletteRoom /></LazyRoute>} />
                  <Route path="poker/lobby" element={<LazyRoute><PokerLobby /></LazyRoute>} />
                  <Route path="poker/table/:code" element={<LazyRoute><PokerTable /></LazyRoute>} />
                  <Route path="defender" element={<LazyRoute><TowerDefense /></LazyRoute>} />
                  <Route path="moba/lobby" element={<LazyRoute><MobaArenaGuard><MobaArenaLobby /></MobaArenaGuard></LazyRoute>} />
                  <Route path="moba/room/:code" element={<LazyRoute><MobaArenaGuard><MobaArenaRoom /></MobaArenaGuard></LazyRoute>} />
                  <Route path="moba/play/:code" element={<LazyRoute><MobaArenaGuard><MobaArenaGame /></MobaArenaGuard></LazyRoute>} />
                  <Route path="moba/ranking" element={<LazyRoute><MobaArenaGuard><MobaArenaRanking /></MobaArenaGuard></LazyRoute>} />
                  <Route path="inventory" element={<LazyRoute><UnifiedInventory /></LazyRoute>} />
                  <Route path="game-market" element={<LazyRoute><UnifiedMarket /></LazyRoute>} />
                  <Route path="story" element={<LazyRoute><StoryMode /></LazyRoute>} />
                  <Route path="story/mission/:missionId" element={<LazyRoute><StoryRouter /></LazyRoute>} />
                  <Route path="transport" element={<LazyRoute><TransportPage /></LazyRoute>} />
                  <Route path="transport/pay/:sessionId" element={<LazyRoute><TransportQrPaymentPage /></LazyRoute>} />
                  <Route path="store/:slug" element={<LazyRoute><StoreFront /></LazyRoute>} />
                  <Route path="store/:slug/invoice/:invoiceNumber" element={
                    <LazyRoute><StoreFrontInvoicePage /></LazyRoute>
                  } />
                  <Route path="store/:slug/qr" element={<LazyRoute><StoreQrPaymentPage /></LazyRoute>} />
                  <Route path="store/:slug/qr/:qrSessionId" element={<LazyRoute><StoreQrPaymentPage /></LazyRoute>} />
                  <Route path="store/:slug/dashboard" element={
                    <ProtectedStoreRouteBySlug requiredPermission="dashboard">
                      <LazyRoute><StoreOwnerDashboard /></LazyRoute>
                    </ProtectedStoreRouteBySlug>
                  } />
                  <Route path="store/:slug/dashboard/batch-close" element={
                    <ProtectedStoreRouteBySlug requiredPermission="dashboard">
                      <LazyRoute><StoreBatchCloseReportPage /></LazyRoute>
                    </ProtectedStoreRouteBySlug>
                  } />
                  <Route path="store/:slug/dashboard/batch-close/:sessionId" element={
                    <ProtectedStoreRouteBySlug requiredPermission="dashboard">
                      <LazyRoute><StoreBatchCloseReportPage /></LazyRoute>
                    </ProtectedStoreRouteBySlug>
                  } />
                  <Route path="store/:slug/pos" element={
                    <ProtectedStoreRouteBySlug requiredPermission="pos">
                      <LazyRoute><POS /></LazyRoute>
                    </ProtectedStoreRouteBySlug>
                  } />
                  <Route path="store/:slug/kitchen" element={
                    <ProtectedStoreRouteBySlug requiredPermission="kitchen">
                      <LazyRoute><KitchenDisplay /></LazyRoute>
                    </ProtectedStoreRouteBySlug>
                  } />
                  <Route path="raffles" element={<GamesAndRafflesGuard><LazyRoute><RafflesLobby /></LazyRoute></GamesAndRafflesGuard>} />
                  <Route path="raffles/my" element={<GamesAndRafflesGuard><LazyRoute><MyRaffles /></LazyRoute></GamesAndRafflesGuard>} />
                  <Route path="raffles/:code" element={<GamesAndRafflesGuard><LazyRoute><RaffleRoom /></LazyRoute></GamesAndRafflesGuard>} />
                  <Route path="admin/*" element={<LazyRoute><Admin /></LazyRoute>} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              )}

              <Suspense fallback={null}>
                <PdfImportFloatingBar />
              </Suspense>
            </div>
            </TutorialProvider>
          </Router>
        </SocketProvider>
        </CartProvider>
      </AuthProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

export default App;
